import React from 'react';
import { Icon } from '@iconify-icon/react';

const rows = [
  {
    question: 'Zero-retention inference by default',
    consumer: { verdict: 'mixed' as const, text: 'Varies by product and settings' },
    chatco: { verdict: 'good' as const, text: 'Yes, when using AWS Bedrock' },
  },
  {
    question: 'No model training on inputs or outputs',
    consumer: { verdict: 'mixed' as const, text: 'Varies by product and settings' },
    chatco: { verdict: 'good' as const, text: 'Yes, through the Bedrock privacy path' },
  },
  {
    question: 'Authenticated private agents',
    consumer: { verdict: 'mixed' as const, text: 'Limited or plan-dependent' },
    chatco: { verdict: 'good' as const, text: 'Built in' },
  },
  {
    question: 'Role-based knowledge access',
    consumer: { verdict: 'mixed' as const, text: 'Limited or plan-dependent' },
    chatco: { verdict: 'good' as const, text: 'Built in' },
  },
  {
    question: 'Encryption in transit and at rest',
    consumer: { verdict: 'mixed' as const, text: 'Varies by product and plan' },
    chatco: { verdict: 'good' as const, text: 'Included' },
  },
  {
    question: 'Public and internal agents in one platform',
    consumer: { verdict: 'bad' as const, text: 'Usually separate products or workflows' },
    chatco: { verdict: 'good' as const, text: 'One governed platform' },
  },
  {
    question: 'Grounded answers with source citations',
    consumer: { verdict: 'mixed' as const, text: 'Limited or inconsistent' },
    chatco: { verdict: 'good' as const, text: 'Designed for verifiable answers' },
  },
];

const verdictIcon = {
  good: { icon: 'tabler:circle-check-filled', className: 'text-emerald-500' },
  bad: { icon: 'tabler:circle-x-filled', className: 'text-red-400' },
  mixed: { icon: 'tabler:help-circle-filled', className: 'text-amber-400' },
};

const PrivacyComparison: React.FC = () => (
  <section
    aria-label="Privacy capabilities compared with Chat.co"
    className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b8862e]">
          Privacy comparison
        </span>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
          Privacy controls you can see and use.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-gray-500">
          Consumer AI privacy varies by product, plan, and settings. Chat.co makes the protected
          Bedrock path and the controls around your knowledge explicit.
        </p>
      </div>

      <div className="mt-12 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b-2 border-gray-100">
              <th className="w-[36%] py-4 pr-4 text-sm font-semibold text-gray-400">
                Privacy capability
              </th>
              <th className="w-[32%] px-4 py-4 text-sm font-semibold text-gray-500">
                Consumer AI tools
              </th>
              <th className="w-[32%] rounded-t-xl bg-[#04182e] px-4 py-4 text-sm font-bold text-white">
                <span className="inline-flex items-center gap-2">
                  <Icon icon="tabler:shield-lock" className="text-base text-[#d3a641]" />
                  Chat.co with AWS Bedrock
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.question} className="border-b border-gray-100">
                <td className="py-5 pr-4 text-sm font-semibold text-gray-900">{row.question}</td>
                <td className="px-4 py-5">
                  <span className="flex items-start gap-2 text-sm text-gray-500">
                    <Icon
                      icon={verdictIcon[row.consumer.verdict].icon}
                      className={`mt-0.5 shrink-0 text-lg ${verdictIcon[row.consumer.verdict].className}`}
                    />
                    {row.consumer.text}
                  </span>
                </td>
                <td
                  className={`bg-[#04182e] px-4 py-5 ${idx === rows.length - 1 ? 'rounded-b-xl' : ''}`}>
                  <span className="flex items-start gap-2 text-sm font-medium text-white">
                    <Icon
                      icon={verdictIcon[row.chatco.verdict].icon}
                      className={`mt-0.5 shrink-0 text-lg ${verdictIcon[row.chatco.verdict].className}`}
                    />
                    {row.chatco.text}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export default PrivacyComparison;
